import React, { Component } from 'react';
import { Checkbox, OverlayTrigger, Tooltip } from 'react-bootstrap';
import _ from 'underscore';
import distance from '@turf/distance';
import intersect from '@turf/intersect';
import { point, polygon } from '@turf/helpers';

import CargomaticSelect from 'components/v2/select';
import GeofenceMap from 'components/modules/locations/GeofenceMap';
import seriesIds from 'constants/RateSeriesIds';
import { useTranslation, withTranslation, Translation } from 'react-i18next';
import { titleize } from 'underscore.string';
import unitConverter from 'convert-units';
import { sluggify } from 'utils/localization/utils';
import { REGIONS, MARKET_REGIONS_US } from 'constants/Regions';
import { withLocaleContext } from 'providers/locale-settings-context';
import { fuelWeeks, fuelDays, fuelMatrixOptions } from '../../../constants/fsc';

const NEARBY = 500; // miles

const marketFormFields = [
  { name: 'Code', field: 'code', type: 'text', localizationSlug: 'CODE' },
  { name: 'Name', field: 'name', type: 'text', localizationSlug: 'NAME' },
  {
    name: 'Distribution Radius',
    field: 'distribution_radius',
    type: 'number',
    localizationSlug: 'DISTRIBUTION_RADIUS',
  },
  {
    name: 'Max Shipment Distance',
    field: 'max_shipment_distance',
    type: 'number',
    localizationSlug: 'MAX_SHIPMENT_DISTANCE',
  },
];

google.maps.Polygon.prototype.my_getBounds = function () {
  const bounds = new google.maps.LatLngBounds();
  this.getPath().forEach((element) => {
    bounds.extend(element);
  });
  return bounds;
};

class CreateMarket extends Component {
  constructor(props) {
    super(props);

    const { market, markets, t, localeData } = props;

    const parseMarketGeofence = (points) => {
      const mapped = points.map((point) => {
        return { lng: point[0], lat: point[1] };
      });

      return [mapped];
    };

    const mapComponent = () => {
      const MAP_REGION = {
        US: { lat: 33.9795304, lng: -118.4145141 },
        AP: { lat: 15.87, lng: 100.9925 },
      };
      return REGION_CODE === REGIONS.AP ? MAP_REGION.AP : MAP_REGION.US;
    };

    this.state = {
      id: market ? market.id : null,
      isActive: market ? market.active : true,
      name: market ? t(market.localizationSlug) : '',
      code: market ? market.code : '',
      center: market ? market.center.coords : [],
      distribution_radius: market
        ? unitConverter(market.distribution_radius)
            .from('mi')
            .to(localeData.distance_units)
            .toFixed(2)
        : 150,
      geofence: market ? market.geofence : {},
      max_shipment_distance: market
        ? unitConverter(market.max_shipment_distance)
            .from('mi')
            .to(localeData.distance_units)
            .toFixed(2)
        : 150,
      latLongs: market
        ? parseMarketGeofence(market.geofence.coordinates[0])
        : [],
      location: market
        ? { lng: market.center.coords[0], lat: market.center.coords[1] }
        : mapComponent(),
      marketsLatLongs: this.parseStaticMarketsGeofences(markets, market),
      overlap: false,
      seriesId: market?.fsc_config?.series_id,
      fuelWeek: market?.fsc_config?.fuelWeek,
      fuelDay: market?.fsc_config?.fuelDay,
      fuelMatrix: market?.fsc_config?.fuelMatrix,
      region: market?.region,
    };
  }

  parseStaticMarketsGeofences = (markets, thisMarket) => {
    const geofenceArray = [];
    markets.forEach((market) => {
      if (market.id !== thisMarket.id) {
        geofenceArray.push(
          market.geofence.coordinates[0].map((points) => {
            return { lng: points[0], lat: points[1] };
          }),
        );
      }
    });
    return geofenceArray;
  };

  handleGeofence = (geofence) => {
    // Get Center from polygon bounds
    const center = geofence.polygon.my_getBounds().getCenter();
    const centerLatLong = [center.lng(), center.lat()];
    // Call has to be done in 2 setState calls.
    // A function triggered off a center change requires geofence to be up to date
    this.setState({ geofence }, () => this.setState({ center: centerLatLong }));
    const newMarket = {
      coordinates: geofence.coordinates,
      center: centerLatLong,
    };
    this.checkForOverlap(newMarket, this.props.markets);
  };

  handlePolygonClear = () => {
    const polygon = this.state.geofence.polygon;
    this.setState(
      { geofence: {}, latLongs: [], center: [], overlap: false },
      () => {
        if (polygon) {
          polygon.setMap(null);
        }
      },
    );
  };

  isValidGooglePolygon = (coordinates) =>
    coordinates.length &&
    coordinates[0].length &&
    coordinates[0][0].length &&
    coordinates[0][0][0].length > 3;

  checkForOverlap = (newMarket, markets) => {
    // const options = { units: 'miles' };
    let overlap = false;
    // Make sure the polygon returned from Google looks OK.
    if (this.isValidGooglePolygon(newMarket.coordinates)) {
      for (let i = 0; i < markets.length - 1; i++) {
        if (
          markets[i].id !== this.props.market.id &&
          distance(point(newMarket.center), point(markets[0].center.coords)) <
            NEARBY
        ) {
          const result = intersect(
            polygon(newMarket.coordinates[0][0]),
            polygon(markets[i].geofence.coordinates),
          );
          if (result) {
            overlap = true;
            break;
          }
        }
      }
    } else {
      console.log('Invalid polygon from Google map library');
    }
    this.setState({ overlap });
  };

  saveEnabled = () => {
    if (
      this.state.name !== '' &&
      this.state.code !== '' &&
      this.state.distribution_radius !== '' &&
      this.state.max_shipment_distance !== '' &&
      !_.isEmpty(this.state.geofence) &&
      this.state.region !== undefined
    ) {
      return true;
    }
    return false;
  };

  coordsUnchanged = () =>
    this.state.id &&
    this.state.geofence.coordinates === this.props.market.geofence.coordinates;

  handleSeriesSelected = (series) => {
    this.setState({ seriesId: series.value });
  };

  handleFuelWeekSelected = ({ value }) => {
    this.setState({ fuelWeek: value });
  };

  handleFuelDaySelected = ({ value }) => {
    this.setState({ fuelDay: value });
  };

  handleRegionSelected = ({ value }) => {
    this.setState({ region: value });
  };

  handleFuelMatrixSelected = ({ value }) => {
    this.setState({ fuelMatrix: value });
  };

  handleSave = () => {
    // If a new geofence has been drawn we need to reformat it.
    const coordinates = this.coordsUnchanged()
      ? this.state.geofence.coordinates
      : _.flatten(this.state.geofence.coordinates, true).pop();

    const market = {
      name: this.state.name,
      localizationSlug: sluggify(this.state.name),
      code: this.state.code,
      active: this.state.isActive,
      distribution_radius: this.state.distribution_radius,
      max_shipment_distance: this.state.max_shipment_distance,
      geofence: { coordinates, type: 'Polygon' },
      fsc_config: {
        series_id: this.state.seriesId,
        fuelWeek: this.state.fuelWeek,
        fuelDay: this.state.fuelDay,
        fuelMatrix: this.state.fuelMatrix,
      },
      center: {
        type: 'Point',
        coords: this.state.center,
      },
      region: this.state.region,
    };

    if (this.state.id) {
      this.props.onEdit(this.state.id, market);
    } else {
      this.props.onSave(market);
    }
    this.props.onHide();
  };

  render() {
    return (
      <div className="p-4">
        <div className="flex flex-wrap -mx-4">
          <div className="lg:w-1/2 w-full px-4">
            <Translation>
              {(t) => {
                return (
                  <div className="flex flex-wrap justify-between items-center">
                    <h4 className="flex-1">{t('CREATE_GEOFENCE_MSG')}</h4>
                    <button
                      className="button button-hollow-red"
                      onClick={this.handlePolygonClear}
                    >
                      {t('CLEAR_MAP')}
                    </button>
                  </div>
                );
              }}
            </Translation>

            <GeofenceMap
              height={500}
              location={this.state.location}
              zoom={6}
              onPolygonComplete={this.handleGeofence}
              latLongs={this.state.marketsLatLongs}
              focusLatLongs={this.state.latLongs}
              canEditPolygon="true"
            />
          </div>
          <div className="lg:w-1/2 w-full px-4">
            <Translation>
              {(t) => (
                <div className="sm:grid sm:grid-cols-4 sm:gap-4 lg:grid-cols-6">
                  {marketFormFields.map((item, index) => {
                    return (
                      <div className="sm:col-span-2" key={index}>
                        <fieldset>
                          <label className="form-label mb-1">
                            {t(item.localizationSlug)}
                          </label>
                          <input
                            type={item.type}
                            value={this.state[item.field]}
                            className="form-field block"
                            onChange={(e) =>
                              this.setState({ [item.field]: e.target.value })
                            }
                          />
                        </fieldset>
                      </div>
                    );
                  })}
                  <div className="sm:col-span-2">
                    <CargomaticSelect
                      showCaption
                      label={t('REGIONS')}
                      selected={this.state.region}
                      items={MARKET_REGIONS_US}
                      textKey="localizationSlug"
                      valueKey="value"
                      onSelect={this.handleRegionSelected}
                    />
                  </div>
                  <div className="sm:col-span-2" />
                  <div className="sm:col-span-2">
                    <CargomaticSelect
                      showCaption
                      label={t('SERIES_ID')}
                      selected={this.state.seriesId}
                      items={seriesIds}
                      onSelect={this.handleSeriesSelected}
                      applyDefaultMargins={false}
                      valueKey="value"
                      textKey="name"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <CargomaticSelect
                      showCaption
                      label={t('FUEL_WEEK')}
                      selected={this.state.fuelWeek}
                      items={fuelWeeks}
                      textKey="localizationSlug"
                      valueKey="value"
                      onSelect={this.handleFuelWeekSelected}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <CargomaticSelect
                      showCaption
                      label={t('FUEL_DAY')}
                      selected={this.state.fuelDay}
                      items={fuelDays}
                      textKey="localizationSlug"
                      valueKey="value"
                      onSelect={this.handleFuelDaySelected}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <CargomaticSelect
                      showCaption
                      label={t('FUEL_MATRIX')}
                      selected={this.state.fuelMatrix}
                      items={fuelMatrixOptions}
                      textKey="localizationSlug"
                      valueKey="value"
                      onSelect={this.handleFuelMatrixSelected}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <fieldset>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="active-tooltip w-full">
                            {t('CUSTOMER_BOOK_SHIPPER_PORTAL_MSG')}
                          </Tooltip>
                        }
                      >
                        <label className="form-label pr-5 mb-4">
                          {titleize(t('ACTIVE'))}{' '}
                          <i className="fa fa-info-circle" />
                        </label>
                      </OverlayTrigger>
                      <Checkbox
                        checked={this.state.isActive}
                        label={t('YES')}
                        onChange={() => {
                          this.setState((prevState) => ({
                            isActive: !prevState.isActive,
                          }));
                        }}
                        type="checkbox"
                      >
                        <span className="form-label">
                          {t('CURRENTLY_ACTIVE')}
                        </span>
                      </Checkbox>
                    </fieldset>
                  </div>
                </div>
              )}
            </Translation>
          </div>
        </div>
        {this.state.overlap && (
          <Translation>
            {(t) => (
              <div className="bg-red-500 text-center h-8">
                <span className="text-white text-lg">
                  {t('MARKET_OVERLAP_MSG')}
                </span>
              </div>
            )}
          </Translation>
        )}
        <div>
          <CreateMarketFooter
            onHide={this.props.onHide}
            onSave={this.handleSave}
            saveEnabled={this.saveEnabled()}
          />
        </div>
      </div>
    );
  }
}

const CreateMarketFooter = (props) => {
  const { t } = useTranslation();
  return (
    <div className="">
      <div className="flex justify-end mt-4">
        <button className="button button-secondary" onClick={props.onHide}>
          {titleize(t('CANCEL'))}
        </button>
        <button
          className="button button-green"
          onClick={props.onSave}
          disabled={!props.saveEnabled}
        >
          {titleize(t('SAVE'))}
        </button>
      </div>
    </div>
  );
};
export default withTranslation()(withLocaleContext(CreateMarket));
